import { Region } from '@/services/auth/types';
import { useTrackEvent } from '@/services/analytics';
import { BOOT_START } from '@/main';
import { useStore } from '@nanostores/react';
import { createContext, useEffect, useRef, useState, useContext } from 'react';
import { accountStore } from '@/services/auth/store';

export enum PerformanceMetric {
  BootStart = 'bootStart',
  Authenticated = 'authenticated',
  ChildAppLoadStart = 'childAppLoadStart',
}

type Metrics = Record<PerformanceMetric, number | null>;
type RecordMetric = (metric: PerformanceMetric) => void;

// Due to the way analytics tracking is implemented, the performance provider
// can only be rendered on the authenticated pages. Until we support anonymous
// events, we'll provide a default empty function for the context rather than
// throw an error when context is not defined.
// eslint-disable-next-line @typescript-eslint/no-empty-function
export const PerformanceContext = createContext<RecordMetric>(() => {});

export function usePerformance() {
  return useContext(PerformanceContext);
}

export function PerformanceProvider({ children }: { children: React.ReactNode }) {
  const track = useTrackEvent();
  const account = useStore(accountStore);
  const isSending = useRef(false);
  const hasBeenSent = useRef(false);
  const [hasTriedToOverrideMetric, setHasTriedToOverrideMetric] = useState(false);
  const [metrics, setMetrics] = useState<Metrics>({
    [PerformanceMetric.BootStart]: BOOT_START,
    [PerformanceMetric.Authenticated]: null,
    [PerformanceMetric.ChildAppLoadStart]: null,
  });

  useEffect(() => {
    if (Object.values(metrics).includes(null) || hasBeenSent.current || isSending.current) {
      return;
    }

    isSending.current = true;

    const calculatedMetrics = {
      timeToAuthenticated: (metrics.authenticated ?? 0) - (metrics.bootStart ?? 0),
      timeToChildAppLoadStart: (metrics.childAppLoadStart ?? 0) - (metrics.bootStart ?? 0),
    };

    track('Performance: Hydra Initial Load', {
      'Account Id': account?.id,
      'Data Center': account?.region === Region.EU ? 'eu' : 'us',
      'Route Name': window.location.pathname,
      'Has Tried To Override Metric': hasTriedToOverrideMetric,
      'Hydra Boot Start': metrics.bootStart ?? 0,
      'Hydra Authenticated': metrics.authenticated ?? 0,
      'Hydra Child App Load Start': metrics.childAppLoadStart ?? 0,
      'Hydra Time To Authenticated': calculatedMetrics.timeToAuthenticated ?? 0,
      'Hydra Time To Child App Load Start': calculatedMetrics.timeToChildAppLoadStart ?? 0,
    }).finally(() => {
      hasBeenSent.current = true;
    });
  }, [hasBeenSent, isSending, metrics, account, hasTriedToOverrideMetric]);

  function saveMetric(metric: PerformanceMetric) {
    // If this metric has alreavy been set, ignore it
    if (metrics[metric] != null) {
      setHasTriedToOverrideMetric(true);
      return;
    }

    setMetrics((previousMetrics) => ({
      ...previousMetrics,
      [metric]: performance.now(),
    }));
  }

  return <PerformanceContext.Provider value={saveMetric}>{children}</PerformanceContext.Provider>;
}
